import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useApiQuery } from '../../../../../../api/hooks';
import apiClient from '../../../../../../api/apiClient';
import { useSmenaStore, usePreloaderStore } from '../../../../../../stores';
import useDemocracyMode from '../../../../../../views/democracy/useDemocracyMode';
import { Endpoints, QueryKey, Defaults, DemocracyStatus, BusinessErrorCode } from '../../../../../../views/democracy/constants';
import { extractErrorMessage, extractErrorCode } from '../../../../../../views/democracy/utils';
import { PlusIcon } from '../../../../model/icons';
import TeamMgmtCard from './TeamMgmtCard';
import TeamEditModal from './TeamEditModal';
import ImportResultModal from './ImportResultModal';
import './teamsMgmt.css';

// Секция «Участники» (команды) в модалке настроек: карточки + добавление/импорт.
export default function TeamsManagement({ democracy }) {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const { isOnline } = useDemocracyMode();
    const smenaId = useSmenaStore(s => s.smenaId);
    const { show, hide } = usePreloaderStore();

    const teamsKey = QueryKey.teams(democracy.id);
    const { data: rawTeams } = useApiQuery(teamsKey, Endpoints.teams.listForDemocracy(democracy.id), { refetchOnMount: 'always' });
    const teams = Array.isArray(rawTeams) ? rawTeams.filter(t => t && t.id != null) : [];

    const [selectedTeamId, setSelectedTeamId] = useState(null);
    const [importResult, setImportResult] = useState(null);

    const invalidate = () => queryClient.invalidateQueries({ queryKey: teamsKey });
    const isDevelop = democracy.status === DemocracyStatus.DEVELOP;

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const [localOrder, setLocalOrder] = useState(null);
    const sortedTeams = [...teams].sort((a, b) => (a.orderId || 0) - (b.orderId || 0));
    const orderedTeams = localOrder
        ? localOrder.map(id => sortedTeams.find(t => t.id === id)).filter(Boolean)
        : sortedTeams;

    const handleDragEnd = async ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const ids = orderedTeams.map(t => t.id);
        const newIds = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id));
        setLocalOrder(newIds);
        show();
        try {
            const resp = await apiClient.put(Endpoints.teams.reorder(democracy.id), { orderedTeamIds: newIds });
            if (Array.isArray(resp.data)) queryClient.setQueryData(teamsKey, resp.data);
            setLocalOrder(null);
            enqueueSnackbar('Порядок команд сохранён', { variant: 'success' });
        } catch (error) {
            setLocalOrder(null);
            enqueueSnackbar(extractErrorMessage(error, 'Не удалось изменить порядок команд'), { variant: 'error' });
        } finally {
            hide();
        }
    };

    const handleAddTeam = async () => {
        show();
        try {
            const fresh = await apiClient.get(Endpoints.teams.listForDemocracy(democracy.id));
            const existing = Array.isArray(fresh.data) ? fresh.data : [];
            const orderId = existing.reduce((m, t) => Math.max(m, (t && t.orderId) || 0), 0) + 1;
            await apiClient.post(Endpoints.teams.createForDemocracy(democracy.id), {
                orderId, name: `Команда ${orderId}`, color: Defaults.teamColor,
                initialBalance: Defaults.initialBalance, initialRating: Defaults.initialRating
            });
            invalidate();
            enqueueSnackbar('Команда создана', { variant: 'success' });
        } catch (error) {
            if (error?.response?.data?.code === BusinessErrorCode.ORDER_ID_TAKEN) {
                enqueueSnackbar('Нумерация устарела, перезагрузите страницу', { variant: 'warning' });
            } else {
                enqueueSnackbar(extractErrorMessage(error, 'Не удалось создать команду'), { variant: 'error' });
            }
        } finally {
            hide();
        }
    };

    const handleImport = async () => {
        if (!smenaId) { enqueueSnackbar('Выберите смену в шапке', { variant: 'warning' }); return; }
        show();
        try {
            const response = await apiClient.post(Endpoints.teams.importFromCamp(democracy.id), null, { params: { smenaId } });
            setImportResult(response.data);
            invalidate();
        } catch (error) {
            const code = extractErrorCode(error);
            const msg = {
                [BusinessErrorCode.IMPORT_NOT_AVAILABLE_IN_STANDALONE]: 'Импорт недоступен в standalone-режиме',
                [BusinessErrorCode.CAMP_DOMAIN_UNAVAILABLE]: 'CampDomainService недоступен. Попробуйте позже',
                [BusinessErrorCode.SESSION_NOT_IN_DEVELOP]: 'Импорт возможен только в статусе DEVELOP'
            }[code];
            enqueueSnackbar(msg || extractErrorMessage(error, 'Не удалось импортировать команды'), { variant: 'error' });
        } finally {
            hide();
        }
    };

    const selectedTeam = teams.find(t => t.id === selectedTeamId) || null;

    return (
        <div>
            <div className="teams-head">
                <div>
                    <span className="kicker" style={{ display: 'block', marginBottom: 5 }}>Команды</span>
                    <div className="ttl">Участники</div>
                </div>
                <div className="right">
                    {isOnline && <button type="button" className="btn btn-ghost btn-sm" onClick={handleImport}>Импорт из смены</button>}
                    <button type="button" className="btn btn-primary btn-sm" onClick={handleAddTeam}>
                        <span className="ic"><PlusIcon /></span>Добавить команду
                    </button>
                </div>
            </div>

            {teams.length === 0 ? (
                <div className="tmg-empty">Команд пока нет. Создайте через «Добавить команду» или импортируйте из смены.</div>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={orderedTeams.map(t => t.id)} strategy={rectSortingStrategy}>
                        <div className="tcards">
                            {orderedTeams.map(team => (
                                <SortableTeamCard
                                    key={team.id}
                                    team={team}
                                    onEdit={isDevelop ? () => setSelectedTeamId(team.id) : null}
                                    onChanged={invalidate}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            {selectedTeam && (
                <TeamEditModal key={selectedTeam.id} team={selectedTeam} isDevelop={isDevelop}
                    onClose={() => setSelectedTeamId(null)} onChanged={invalidate} />
            )}
            {importResult && <ImportResultModal result={importResult} onClose={() => setImportResult(null)} />}
        </div>
    );
}

function SortableTeamCard(props) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.team.id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
    return (
        <div ref={setNodeRef} style={style}>
            <TeamMgmtCard {...props} dragHandleProps={{ ...attributes, ...listeners }} />
        </div>
    );
}

SortableTeamCard.propTypes = {
    team: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired
};

TeamsManagement.propTypes = {
    democracy: PropTypes.shape({ id: PropTypes.number.isRequired, status: PropTypes.string }).isRequired
};
